import { useEffect, useState, useContext, useMemo, useCallback } from "react";
import VendorHelper from "helpers/VendorHelper";
import { useParams } from "react-router-dom";
import styles from "./styles.module.css";
import {
  InputAdornment,
  IconButton,
  Paper,
  TablePagination,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import DataTable from "components/utils/DataTable";
import Info from "components/utils/Alert/Info";
import AddPaymentToVendorDialog from "components/popups/AddPaymentToVendor/AddPaymentToVendor";
import DateTimeHelpers from "helpers/DateTimeHelpers";
import SearchIcon from "images/svg/search.svg";
import { ReactComponent as AddPayment } from "images/svg/addPayment.svg";
import { ReactComponent as EditIcon } from "images/svg/edit-2.svg";
import EditVendor from "components/popups/EditVendor";
import VendorContext from "context/VendorContext";
import { ReactComponent as ArrowIcon } from "images/svg/bold-arrow.svg";
import CustomDateRangePicker from "components/utils/CustomFields/CustomDateRangePicker";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import * as NumberFormatHelper from "helpers/NumberFormatHelper";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "const";
import toast from "react-hot-toast";
import ConfirmPopup from "components/popups/Alert/ConfirmPopup";
import BackIcon from "images/svg/back-icon.svg";

function VendorDetails() {
  const { updateVendor, fetchVendors, addPaymentToVendor, deleteVendor, vendorJournals, fetchVendorJournals } =
    useContext(VendorContext);
  const navigate = useNavigate();

  const [loading, setloading] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [filterText, setFilterText] = useState("");
  const [vendorJournalsPagination, setVendorJournalsPagination] = useState({
    page: 0,
    limit: 20,
  });
  const [dateRange, setDateRange] = useState([new Date(new Date().setDate(new Date().getDate() - 7)), new Date()]);
  const [paymentTab, setPaymentTab] = useState(false);
  const [editVendor, setEditVendor] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const { vendorId } = useParams();

  const filteredJournals = useMemo(() => {
    if (!vendorJournals || !vendorJournals.length) return [];
    if (!filterText) return vendorJournals;

    const lowerFilter = filterText.toLowerCase();
    const list = vendorJournals.filter((item) => {
      const date = new Date(item.time);
      const formattedDate = DateTimeHelpers.convertDateToUtcDmy(date);

      const match = item.description?.match(/"([^"]+)"/);
      const displayDesc = (match ? match[1] : item.description || "").toLowerCase();

      return formattedDate.toLowerCase().includes(lowerFilter) || displayDesc.includes(lowerFilter);
    });

    return list.sort((a, b) => new Date(b.time || 0) - new Date(a.time || 0));
  }, [vendorJournals, filterText]);

  const paginatedJournals = useMemo(() => {
    const start = vendorJournalsPagination.page * vendorJournalsPagination.limit;
    return filteredJournals.slice(start, start + vendorJournalsPagination.limit);
  }, [filteredJournals, vendorJournalsPagination.page, vendorJournalsPagination.limit]);

  const getBalance = (data) => {
    const balance = Number(data?.balance) || 0;
    const balanceLabel = NumberFormatHelper.customerAmountFormat(balance);
    const balanceStyle = balance >= 0 ? { color: "green" } : { color: "red" };
    return (
      <Typography variant="body2" sx={balanceStyle} gutterBottom>
        {balanceLabel}
      </Typography>
    );
  };

  const headerData = [
    {
      label: "Date",
      id: "Date",
      type: "callback",
      width: "150px",
      viewRender: (item) => {
        const date = new Date(item.time);
        return DateTimeHelpers.convertDateToUtcDmy(date);
      },
    },
    {
      label: "Time",
      id: "time",
      type: "callback",
      width: "150px",
      viewRender: (item) => {
        const date = item.time.split("T")[1].split(".")[0];
        return DateTimeHelpers.formatServerTimeSegment(date);
      },
    },
    {
      label: "Description",
      id: "description",
      type: "callback",
      viewRender: (item) => {
        const match = item.description?.match(/"([^"]+)"/);
        return match ? match[1] : item.description || "-";
      },
    },
    {
      label: "Credit",
      id: "credit",
      type: "text",
      width: "150px",
    },
    {
      label: "Debit",
      id: "debit",
      type: "text",
      width: "150px",
    },
    {
      label: "Balance",
      id: "balance",
      type: "callback",
      width: "150px",
      viewRender: (data) => getBalance(data),
    },
  ];

  const getVendorJournals = useCallback(
    async ({ _fromDate = dateRange[0], _toDate = dateRange[1] } = {}) => {
      if (!vendorId) return;
      setloading(true);
      try {
        const params = {
          start_time: DateTimeHelpers.getStartOfDayISO(_fromDate),
          end_time: DateTimeHelpers.getEndOfDayISO(_toDate),
        };

        await fetchVendorJournals(vendorId, params);
      } catch (error) {
        console.error(error);
      } finally {
        setloading(false);
      }
    },
    [vendorId, dateRange, fetchVendorJournals],
  );

  const getVendorDetails = useCallback(async () => {
    try {
      const res = await VendorHelper.getVendor(vendorId);
      setVendor(res);
    } catch (error) {
      console.error(error.message);
    }
  }, [vendorId]);

  const togglePaymentTab = () => {
    setPaymentTab((prevValue) => !prevValue);
  };
  const toggleEditVendorTab = () => {
    setEditVendor((prevValue) => !prevValue);
  };

  const handleAddPaymentToVendor = async (data) => {
    setloading(true);
    try {
      await addPaymentToVendor(vendorId, data);
      setVendorJournalsPagination((prev) => ({
        ...prev,
        page: 0,
      }));
      getVendorDetails();
      await getVendorJournals();
    } catch (error) {
      console.error(error.message);
    }
    setloading(false);
  };

  const handleChangeVendorJournalPage = (event, newPage) => {
    setVendorJournalsPagination((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleChangeVendorRowsPerPage = (event) => {
    const pageRowLimit = parseInt(event.target.value, 10);
    setVendorJournalsPagination({
      limit: pageRowLimit,
      page: 0,
    });
  };

  const handleEditVendor = async (updatedData) => {
    setloading(true);
    try {
      const res = await updateVendor(vendorId, updatedData);
      if (res) {
        setVendor(res);
        toast.success("Vendor updated successfully");
      }
      setEditVendor(false);
      await fetchVendors();
    } catch (error) {
      console.error("Failed to update vendor:", error.message);
      toast.error("Failed to update vendor");
    }
    setloading(false);
  };

  const handleDeleteVendor = async () => {
    try {
      setloading(true);
      await deleteVendor(vendorId);
      toast.success("Vendor deleted successfully");
      navigate(ROUTES.VENDORS);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete vendor");
    } finally {
      setloading(false);
    }
  };

  useEffect(() => {
    getVendorDetails();
  }, [getVendorDetails]);

  useEffect(() => {
    setVendorJournalsPagination((prev) => ({ ...prev, page: 0 }));
    getVendorJournals();
  }, [getVendorJournals]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <div className={styles.leftHeader}>
            <button
              type="button"
              className={styles.backButton}
              onClick={() => navigate(ROUTES.VENDORS)}
              aria-label="Back to Vendors"
            >
              <img src={BackIcon} alt="Back to Vendors" />
            </button>

            <div className={styles.header}>
              <h2>Vendor Details</h2>
              <h5>
                Vendor <ArrowIcon style={{ margin: "0 4px" }} />
                {vendor?.name}
              </h5>
            </div>
          </div>

          <div className={styles.contentWrapper}>
            <TextField
              placeholder="Search"
              size="small"
              className={styles.searchField}
              value={filterText}
              onChange={(event) => {
                setFilterText(event.target.value);
                setVendorJournalsPagination((prev) => ({ ...prev, page: 0 }));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" style={{ padding: "0" }}>
                      <img src={SearchIcon} alt="Search" className={styles.icon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className={styles.bar}> </div>

            <div className={styles.datePicker}>
              <CustomDateRangePicker
                value={dateRange}
                onChange={(newRange) => {
                  setDateRange(newRange);
                }}
              />
            </div>

            <div className={styles.bar}> </div>

            <Button className={styles.addCustBtn} onClick={() => toggleEditVendorTab()}>
              <EditIcon className={styles.icon} />
              Edit
            </Button>

            <Button className={styles.addCustBtn} onClick={() => togglePaymentTab()}>
              <AddPayment className={styles.icon} />
              Add Payment
            </Button>
          </div>
        </div>

        <div className={styles.tableContainer}>
          {loading ? (
            <SkeletonLoading isOpen={true} count={3} height={100} xs={12} />
          ) : (
            <TableContainer component={Paper} className={styles.overViewTable}>
              <Table size="small" aria-label="vendor details">
                <TableBody>
                  <TableRow className={styles.tableRow}>
                    <TableCell className={styles.tableCell} style={{ borderTopLeftRadius: "15px" }}>
                      <span className={styles.tableCellHead}>Name</span>
                      <span className={styles.tableCellValue}>{vendor?.name || ""}</span>
                    </TableCell>

                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Code</span>
                      <span className={styles.tableCellValue}>{vendor?.code || "-"}</span>
                    </TableCell>

                    <TableCell className={styles.tableCell} style={{ borderTopRightRadius: "15px" }}>
                      <span className={styles.tableCellHead}>Mobile</span>
                      <span className={styles.tableCellValue}>{vendor?.mobile || "-"}</span>
                    </TableCell>
                  </TableRow>

                  <TableRow className={styles.tableRow}>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Location</span>
                      <span className={styles.tableCellValue}>{vendor?.address || "-"}</span>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <span className={styles.tableCellHead}>Balance</span>
                      <span
                        className={styles.tableCellValue}
                        style={{ color: (vendor?.balance || 0) >= 0 ? "green" : "red" }}
                      >
                        {NumberFormatHelper.customerAmountFormat(vendor?.balance || 0)}
                      </span>
                    </TableCell>
                    <TableCell className={`${styles.tableCell} ${styles.emptyCell}`}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <div className={styles.journalWrapper}>
            {loading ? (
              <SkeletonLoading isOpen={true} count={8} height={45} xs={12} />
            ) : filteredJournals && filteredJournals.length ? (
              <Paper
                elevation={0}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div style={{ flex: 1, overflowY: "auto" }}>
                  <DataTable columns={headerData} rows={paginatedJournals} />
                </div>
                <div className={styles.pagination}>
                  <TablePagination
                    rowsPerPageOptions={[20, 50]}
                    component="div"
                    count={filteredJournals.length}
                    rowsPerPage={vendorJournalsPagination.limit}
                    page={vendorJournalsPagination.page}
                    onPageChange={handleChangeVendorJournalPage}
                    onRowsPerPageChange={handleChangeVendorRowsPerPage}
                  />
                </div>
              </Paper>
            ) : (
              !loading && (
                <Info
                  title={"No transactions to list"}
                  content={
                    "You have no transactions to list with current filter configuration. Please clear the filters"
                  }
                />
              )
            )}
          </div>
        </div>

        {paymentTab && (
          <AddPaymentToVendorDialog
            onClose={togglePaymentTab}
            addPaymentToVendor={handleAddPaymentToVendor}
            currentBalance={vendor?.balance || 0}
            vendor={vendor}
          />
        )}
        {editVendor && (
          <EditVendor
            closeDialog={() => setEditVendor(false)}
            editVendorHandler={handleEditVendor}
            vendor={vendor}
            onClose={toggleEditVendorTab}
          />
        )}
      </div>

      <ConfirmPopup
        data={deleteConfirm}
        handleClose={() => setDeleteConfirm(false)}
        handleOkay={handleDeleteVendor}
        alertTitle="Delete Vendor"
        alertContent={`Are you sure you want to delete vendor "${vendor?.name}"? This action cannot be undone.`}
        successBtnName="Delete"
      />
    </>
  );
}

export default VendorDetails;
